import { ErrorHandler, inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';

import { AppError, ErrorFactory, NetworkError, ServerError } from '../../shared/errors/app-error';
import { ApplicationInsightsService } from '../services/application-insights.service';
import { LoaderService } from '../services/loader.service';

/**
 * Global error handler for unhandled errors
 * Catches errors that aren't caught by HTTP interceptors
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly loaderService = inject(LoaderService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly appInsightsService = inject(ApplicationInsightsService);

  handleError(error: unknown): void {
    // Hide loader if showing
    this.loaderService.hide();

    // Convert to AppError
    const appError = ErrorFactory.fromHttpError(error);

    // Log error for debugging
    this.logError(appError, error);

    // Report to error tracking service (if configured)
    this.reportError(appError);

    // Handle specific error types with navigation
    this.handleErrorNavigation(appError);

    // Re-throw for Angular's default error handling in development
    if (!this.isProduction()) {
      console.error('GlobalErrorHandler:', error);
    }
  }

  private logError(appError: AppError, originalError: unknown): void {
    const errorInfo = appError.toErrorInfo();

    // In production, you might want to send to a logging service
    console.group(`[${appError.name}] ${errorInfo.code} (${errorInfo.statusCode})`);
    console.error('Message:', errorInfo.message);
    console.error('Title:', errorInfo.title);
    console.error('Timestamp:', errorInfo.timestamp.toISOString());
    console.error('Trace ID:', errorInfo.traceId || 'N/A');
    console.error('Original Error:', originalError);
    console.groupEnd();
  }

  private reportError(appError: AppError): void {
    // Only report server errors and unknown errors
    if (!appError.shouldReport()) {
      return;
    }

    const errorInfo = appError.toErrorInfo();
    const originalError =
      appError.originalError instanceof Error
        ? appError.originalError
        : new Error(appError.message);

    this.appInsightsService.trackException(originalError, undefined, {
      errorCode: errorInfo.code,
      statusCode: String(errorInfo.statusCode),
      title: errorInfo.title,
      traceId: errorInfo.traceId || 'N/A',
      url: typeof window !== 'undefined' ? window.location.href : '',
    });
  }

  private handleErrorNavigation(appError: AppError): void {
    // Run navigation in NgZone to ensure proper routing
    this.ngZone.run(() => {
      if (appError instanceof NetworkError) {
        // For network errors, navigate to offline page or show banner
        // Could navigate to /offline or show a persistent banner
        console.warn('Network error - consider showing offline indicator');
      } else if (appError instanceof ServerError) {
        // For server errors, optionally navigate to error page
        // This is typically handled by the component that triggered the error
        // but we could force navigation for unhandled server errors
        console.warn('Unhandled server error');
      }
    });
  }

  private isProduction(): boolean {
    // Check if we're in production mode
    return (
      typeof window !== 'undefined' &&
      window.location.hostname !== 'localhost' &&
      !window.location.hostname.includes('127.0.0.1')
    );
  }
}
